/** Branch.io app-config editor — shared by BRANCHIO_APP_SETUP and the Android/iOS
 *  follow-ups.
 *
 *  Fetches the operator's LIVE Branch app config via the Branch App API (branch_config),
 *  shows per-aspect completion, and edits the fields Branch's PUT accepts
 *  (save_branch_config — the allowlist comes from the server). `editKeys` scopes which
 *  editable fields to show (default: all); `readKeys` surfaces read-only fields (e.g. the
 *  SHA256 fingerprint / Apple Team ID) that are set in the Branch dashboard;
 *  `completionKeys` scopes the completion chips. dev_email is always sent (Branch requires
 *  it) even when not shown.
 *
 *  On a sandbox the live config is read but never written: Branch's PUT is a vendor write, refused for
 *  every sandbox (SBX D12, O27; metaplan S8), so the fields are read-only and Save is not offered —
 *  the sandbox's own Branch app is edited in the Branch dashboard.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import * as topsApi from '@/api/tourOperators';
import type { PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { formatValue } from '@/features/preflight/detailFormat';

import { copyOnlyActions } from '../tenantLinks';

const FIELD_LABELS: Record<string, string> = {
  app_name: 'App name',
  dev_name: 'Developer name',
  dev_email: 'Developer email',
  android_url: 'Android redirect / Play link',
  android_uri_scheme: 'Android URI scheme',
  ios_url: 'iOS redirect / App Store link',
  ios_uri_scheme: 'iOS URI scheme',
  ios_store_country: 'iOS store country',
  web_url: 'Default web URL',
  text_message: 'SMS text',
  og_app_id: 'OG app id',
  og_title: 'OG title',
  og_description: 'OG description',
  og_image_url: 'OG image URL',
  // read-only (not in the PUT allowlist — set in the Branch dashboard)
  sha256_cert_fingerprints: 'SHA256 cert fingerprints',
  android_package_name: 'Android package name',
  ios_team_id: 'Apple App Prefix / Team ID',
  ios_bundle_id: 'iOS bundle ID',
};

const COMPLETION_LABELS: Record<string, string> = {
  default_url_set: 'Default URL',
  android_redirect_set: 'Android link',
  ios_redirect_set: 'iOS link',
  android_bundle_set: 'Android bundle ID',
  ios_bundle_set: 'iOS bundle ID',
  android_fingerprint_set: 'Android cert fingerprint',
  ios_team_id_set: 'Apple App Prefix',
  og_configured: 'Open Graph',
};

function asStr(v: unknown): string {
  if (typeof v === 'string') return v;
  return v == null ? '' : String(v);
}

/** Branch's app config nests (e.g. `sha256_cert_fingerprints` is a list); the
 *  shared formatter names the contents instead of dumping raw JSON into a cell. */
function showValue(v: unknown): string {
  return typeof v === 'object' && v !== null ? formatValue(v) : asStr(v);
}

export function BranchConfigEditor({
  item,
  topId,
  canManage,
  onChanged,
  editKeys,
  readKeys,
  completionKeys,
  showSubdomain = false,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
  /** Subset of the PUT allowlist to expose as editable (default: all server-editable). */
  editKeys?: string[];
  /** Read-only Branch fields to surface prominently (set in the dashboard). */
  readKeys?: string[];
  /** Completion flags to show as chips (default: all). */
  completionKeys?: string[];
  showSubdomain?: boolean;
}) {
  const snackbar = useSnackbar();
  const [edits, setEdits] = useState<Record<string, string>>({});
  // The operator pages already hold the detail under this key; a sandbox may read, never write.
  const tenant = useQuery({ queryKey: ['tourOperators', 'detail', topId], queryFn: () => topsApi.retrieve(topId) });
  const isSandbox = tenant.data?.environment === 'sandbox';
  const canWrite = canManage && !isSandbox;
  const subdomain = typeof item.detail.subdomain === 'string' ? item.detail.subdomain : '';
  const keySet = item.detail.branchio_key_set === true && item.detail.branchio_secret_set === true;
  const enabled = canManage && keySet;

  const query = useQuery({
    queryKey: ['preflight', 'branchConfig', topId, item.id],
    queryFn: () => items.fetchBranchConfig(topId, item.id),
    enabled,
  });

  const data = query.data;
  const cfg = data?.ok ? (data.config ?? {}) : {};
  const serverEditable = (data?.ok && data.editable_fields) || [];
  const shownEditable = editKeys ? editKeys.filter((k) => serverEditable.includes(k)) : serverEditable;
  const completion = (data?.ok && data.completion) || {};
  const compKeys = completionKeys ?? Object.keys(COMPLETION_LABELS);
  const value = (f: string) => edits[f] ?? asStr(cfg[f]);
  const devEmailMissing = value('dev_email').trim() === '';

  const save = useMutation({
    mutationFn: () => {
      const values: Record<string, unknown> = {};
      for (const f of shownEditable) values[f] = value(f);
      values.dev_email = value('dev_email'); // required by Branch; preserved if not shown/edited
      return items.saveBranchConfig(topId, item.id, values);
    },
    onSuccess: (res) => {
      if (res.ok) {
        snackbar.show('Branch config saved.', 'success');
        setEdits({});
        void query.refetch();
        onChanged();
      } else {
        snackbar.show(res.detail || 'Save failed.', 'error');
      }
    },
    onError: (e) => snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error'),
  });

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1}>
        {showSubdomain && (
          <Typography variant="body2">
            Create the Branch app with subdomain{' '}
            <Box component="code" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {subdomain || '—'}
            </Box>
            .
          </Typography>
        )}

        {!keySet && (
          <Chip
            size="small"
            variant="outlined"
            label="Keys not set yet — mint them, then “Configure Branch.io keys” to load the app config"
            sx={{ alignSelf: 'flex-start' }}
          />
        )}
        {keySet && !canManage && (
          <Typography variant="caption" color="text.secondary">
            Branch keys configured. Manage permission is required to load + edit the app config.
          </Typography>
        )}

        {enabled && query.isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              Loading the live Branch config…
            </Typography>
          </Box>
        )}

        {enabled && data && !data.ok && (
          <Alert
            severity={data.reason === 'no_keys' ? 'info' : 'warning'}
            action={
              <Button size="small" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {data.reason === 'no_keys'
              ? 'Branch keys are not configured for this operator.'
              : `Couldn’t load the Branch config: ${data.detail || 'unknown error'}`}
          </Alert>
        )}

        {enabled && data?.ok && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {compKeys.map((k) => {
                const done = completion[k] === true;
                return (
                  <Chip
                    key={k}
                    size="small"
                    variant="outlined"
                    color={done ? 'success' : 'default'}
                    icon={done ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
                    label={COMPLETION_LABELS[k] ?? k}
                  />
                );
              })}
            </Box>

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">
                Editable Branch settings
              </Typography>
            </Divider>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
              {shownEditable.map((f) => (
                <TextField
                  key={f}
                  size="small"
                  fullWidth
                  label={FIELD_LABELS[f] ?? f}
                  value={value(f)}
                  required={f === 'dev_email'}
                  error={f === 'dev_email' && devEmailMissing}
                  disabled={!canWrite || save.isPending}
                  onChange={(e) => setEdits((prev) => ({ ...prev, [f]: e.target.value }))}
                />
              ))}
            </Box>

            {readKeys && readKeys.length > 0 && (
              <>
                <Divider textAlign="left">
                  <Typography variant="caption" color="text.secondary">
                    Read-only (set in the Branch dashboard)
                  </Typography>
                </Divider>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 1.5, rowGap: 0.25 }}>
                  {readKeys.map((f) => (
                    <Fragment key={f}>
                      <Typography variant="caption" color="text.secondary">
                        {FIELD_LABELS[f] ?? f}
                      </Typography>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                        {/* Fingerprints and bundle ids are pasted into other
                            consoles verbatim; a mask yields no copy. */}
                        <ValueActions actions={copyOnlyActions(showValue(cfg[f]), `Copy ${FIELD_LABELS[f] ?? f}`)}>
                          {showValue(cfg[f]) || '— not set'}
                        </ValueActions>
                      </Typography>
                    </Fragment>
                  ))}
                </Box>
              </>
            )}

            {canManage && isSandbox && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} data-sandbox-branch-readonly>
                  A sandbox cannot change its Branch app from here: vendor writes are refused for sandboxes. Edit
                  the sandbox’s own Branch app in the Branch dashboard; reading its config here still works.
                </Typography>
                <Button size="small" disabled={query.isFetching} onClick={() => void query.refetch()}>
                  Refresh
                </Button>
              </Stack>
            )}
            {canWrite && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={save.isPending || devEmailMissing}
                  onClick={() => save.mutate()}
                >
                  Save to Branch
                </Button>
                <Button size="small" disabled={query.isFetching || save.isPending} onClick={() => void query.refetch()}>
                  Refresh
                </Button>
              </Stack>
            )}
            {canWrite && devEmailMissing && (
              <Typography variant="caption" color="error">
                Branch requires a developer email (set it in the Branch app setup step).
              </Typography>
            )}

            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  All Branch settings (read-only)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'max-content 1fr',
                    columnGap: 1.5,
                    rowGap: 0.25,
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {Object.entries(cfg).map(([k, v]) => (
                    <Fragment key={k}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'inherit' }}>
                        {k}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'inherit', wordBreak: 'break-all' }}>
                        <ValueActions actions={copyOnlyActions(showValue(v), `Copy ${k}`)}>
                          {showValue(v) || '—'}
                        </ValueActions>
                      </Typography>
                    </Fragment>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
      </Stack>
    </Box>
  );
}
